import { CircleProgress } from "@/components/common/CircleProgress"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Link } from "react-router"
import type { CountTotal } from "../../../api/types"
import { DualValue } from "./DualValue"
import { formatInt, localeForLanguage } from "./dashboardStore"

export interface CoverageTileDef<Stats> {
  labelKey: TranslationKeys
  subKey: TranslationKeys
  value: (s: Stats) => CountTotal
  to: string
  critical?: boolean
  /** Optional extra subtitle lines, each rendered under subKey only while its
   *  count is > 0 (e.g. the never-asked tile's "N without an email address"
   *  and "N undeliverable" hints) — translated as `translate(key, { n })`. */
  extraSubs?: Array<{
    key: TranslationKeys
    count: (s: Stats) => number | undefined
  }>
}

export interface CoverageMetricDef<Stats> {
  value: (s: Stats) => CountTotal
  /** Label under the donut / secondary number (e.g. "products fully covered"). */
  labelKey: TranslationKeys
  /** Compact label for the absolute-figures popover row (e.g. "Products"). */
  popLabelKey: TranslationKeys
}

export interface CoverageEmptyStateDef<Stats> {
  predicate: (s: Stats) => boolean
  messageKey: TranslationKeys
  ctaKey: TranslationKeys
  ctaTo: string
}

export interface CoverageBoardProps<Stats> {
  stats: Stats
  /** Donut-ring metric (articles / products). */
  primary: CoverageMetricDef<Stats>
  /** Big-number metric next to the donut (suppliers / customers). */
  secondary: CoverageMetricDef<Stats>
  popAbsoluteKey: TranslationKeys
  /** Popover row label shown under each pipeline tile's count. */
  tilePopLabelKey: TranslationKeys
  tiles: CoverageTileDef<Stats>[]
  /** Onboarding hint shown instead of the donut when the primary metric has
   * no denominator yet (only the customer dashboard uses this). */
  emptyState?: CoverageEmptyStateDef<Stats>
}

/**
 * Coverage widget layout shared by the supplier and customer Studio
 * dashboards — a donut + secondary metric on the left, four pipeline tiles
 * on the right. Both studios differ only in which stats fields feed the
 * donut/tiles and their copy/links, which is why every studio-specific bit
 * is passed in as config rather than hardcoded here.
 */
export function CoverageBoard<Stats>({
  stats,
  primary,
  secondary,
  popAbsoluteKey,
  tilePopLabelKey,
  tiles,
  emptyState,
}: CoverageBoardProps<Stats>) {
  const { translate, currentLanguage } = useTranslation()
  const locale = localeForLanguage(currentLanguage)
  const primaryValue = primary.value(stats)
  const secondaryValue = secondary.value(stats)
  const pct = primaryValue.total
    ? (primaryValue.count / primaryValue.total) * 100
    : 0
  const showEmptyState = emptyState?.predicate(stats) ?? false

  return (
    <div className="flex flex-wrap items-center gap-x-8 gap-y-6">
      {showEmptyState && emptyState ? (
        <div className="flex min-w-[260px] flex-1 basis-[260px] flex-col items-start gap-3 rounded-lg border border-white/10 bg-white/2 p-4">
          <div className="text-sm text-grey5">
            {translate(emptyState.messageKey)}
          </div>
          <Link
            to={emptyState.ctaTo}
            className="text-sm font-semibold text-white underline decoration-grey5 underline-offset-4 hover:decoration-white"
          >
            {translate(emptyState.ctaKey)}
          </Link>
        </div>
      ) : (
        <HoverCard openDelay={150}>
          <HoverCardTrigger asChild>
            <div
              tabIndex={0}
              className="flex items-center gap-6 rounded-lg focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <CircleProgress
                value={pct}
                maxValue={100}
                size={168}
                strokeWidth={13}
                aria-label={translate(primary.labelKey)}
                centerContent={
                  <div className="text-center">
                    <DualValue
                      count={primaryValue.count}
                      total={primaryValue.total}
                      className="text-[34px] tracking-tight text-white"
                    />
                    <div className="mt-1 max-w-[120px] text-xs text-grey5">
                      {translate(primary.labelKey)}
                    </div>
                  </div>
                }
              />
              <div>
                <DualValue
                  count={secondaryValue.count}
                  total={secondaryValue.total}
                  className="text-[32px] tracking-tight text-white"
                />
                <div className="mt-1.5 max-w-[160px] text-xs text-grey5">
                  {translate(secondary.labelKey)}
                </div>
              </div>
            </div>
          </HoverCardTrigger>
          <HoverCardContent
            align="start"
            className="w-64 border-grey4 bg-grey1"
          >
            <div className="mb-2 text-[10px] uppercase tracking-wider text-grey5">
              {translate(popAbsoluteKey)}
            </div>
            <div className="flex items-baseline justify-between gap-4 text-sm">
              <span className="text-xs text-grey5">
                {translate(primary.popLabelKey)}
              </span>
              <span className="font-semibold tabular-nums text-white">
                {formatInt(primaryValue.count, locale)} /{" "}
                {formatInt(primaryValue.total, locale)}
              </span>
            </div>
            <div className="mt-1.5 flex items-baseline justify-between gap-4 text-sm">
              <span className="text-xs text-grey5">
                {translate(secondary.popLabelKey)}
              </span>
              <span className="font-semibold tabular-nums text-white">
                {formatInt(secondaryValue.count, locale)} /{" "}
                {formatInt(secondaryValue.total, locale)}
              </span>
            </div>
          </HoverCardContent>
        </HoverCard>
      )}
      <div className="grid min-w-0 flex-1 basis-[300px] grid-cols-2 gap-3">
        {tiles.map((tile) => {
          const v = tile.value(stats)
          return (
            <HoverCard key={tile.labelKey} openDelay={150}>
              <HoverCardTrigger asChild>
                <Link
                  to={tile.to}
                  className="rounded-lg border border-white/10 bg-white/2 p-4 text-left transition-colors hover:border-white/20 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="text-[10px] uppercase tracking-wide text-grey5">
                    {translate(tile.labelKey)}
                  </div>
                  <DualValue
                    count={v.count}
                    total={v.total}
                    className={cn(
                      "mt-2.5 block text-[28px] leading-none tracking-tight",
                      tile.critical ? "text-redLight" : "text-white",
                    )}
                  />
                  <div className="mt-2 text-xs text-grey5">
                    {translate(tile.subKey)}
                  </div>
                  {tile.extraSubs?.map((extra) => {
                    const n = extra.count(stats) ?? 0
                    return n > 0 ? (
                      <div key={extra.key} className="mt-1 text-xs text-grey5">
                        {translate(extra.key, { n })}
                      </div>
                    ) : null
                  })}
                </Link>
              </HoverCardTrigger>
              <HoverCardContent
                align="start"
                className="w-56 border-grey4 bg-grey1"
              >
                <div className="flex items-baseline justify-between gap-4 text-sm">
                  <span className="text-xs text-grey5">
                    {translate(tilePopLabelKey)}
                  </span>
                  <span className="font-semibold tabular-nums text-white">
                    {formatInt(v.count, locale)} / {formatInt(v.total, locale)}
                  </span>
                </div>
              </HoverCardContent>
            </HoverCard>
          )
        })}
      </div>
    </div>
  )
}
